"use client";

import * as React from "react";
import { Plus, Minus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqPair {
  id: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqPair[] = [{
  id: "how-is-this-different",
  question: "How is this different from the online homework platforms we already use?",
  answer: "Most platforms mark the final answer and tell you what a student got wrong. Setura reads the working and tells you why: whether the mark was lost to missing content knowledge or to a slip in method. The two need different fixes, and a score alone can't tell them apart."
}, {
  id: "how-accurate-is",
  question: "How accurate is the diagnosis, and can I overrule it?",
  answer: "Every diagnosis is tied to a specific line of working and a specific mark-scheme criterion, so you can check the reasoning, not just the verdict. Teachers can confirm or change any diagnosis, and those corrections make Setura more accurate. We're validating accuracy with our first pilot schools and will share the results."
}, {
  id: "does-it-add",
  question: "Does it add to my workload?",
  answer: "Students can type their working into Setura step by step, or you can upload their completed exam papers as they are, with no need to transcribe anything. Setura reads the working, matches it to the mark scheme and gives you the diagnosis for each student and the patterns across the class. The aim is to replace the time you spend working out why a class is losing marks, not to add another dashboard to check."
}, {
  id: "does-setura-tell",
  question: "Does Setura tell students the answer?",
  answer: "No. Setura diagnoses, it doesn't tutor. On a knowledge gap, it points the student to material their teacher has chosen. On a process error, it flags that something has gone wrong without saying what, so the student learns to find and fix their own mistakes."
}, {
  id: "which-subjects",
  question: "Which subjects and exam boards does it cover?",
  answer: "We're starting with GCSE maths for AQA, Edexcel and OCR, where mark schemes are the most structured. Setura is built to read any criteria-based mark scheme, and other subjects will follow."
}, {
  id: "what-happens",
  question: "What happens to student data?",
  answer: "Student data is processed under UK GDPR, stored on UK servers, and covered by a data processing agreement with each school. Schools own their data and can ask us to delete it at any time. We use anonymised data to improve the accuracy of Setura's diagnosis, and never sell data or share it with third parties."
}, {
  id: "what-does-a-pilot",
  question: "What does a pilot involve?",
  answer: "One class for one half-term. We set it up with you and show you what Setura finds in your students' working. If it's useful, we extend to more classes and the rest of the course, and build it into how your department works long term."
}];
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="faq" data-nav="light" ref={ref} className="bg-background text-foreground border-b border-border">
      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="mb-12 md:mb-16">
          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">
            Common Questions
          </p>
          <h2 className="font-default text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground">
            Questions from the classroom.
          </h2>
        </div>

        <Accordion type="single" collapsible className="w-full divide-y divide-border border-y border-border">
          {FAQ_ITEMS.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-none py-1 transition-all duration-500 ease-out" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          transitionDelay: `${Math.min(i * 80, 500)}ms`
        }}>
              <AccordionTrigger className="text-left font-default text-base sm:text-lg font-medium text-foreground py-5 hover:text-primary transition-colors hover:no-underline [&>svg]:hidden [&[data-state=open]_.faq-plus]:hidden [&[data-state=closed]_.faq-minus]:hidden">
                <span>{item.question}</span>
                <span className="shrink-0 text-muted-foreground ml-4 mt-0.5">
                  <Plus className="faq-plus size-4" />
                  <Minus className="faq-minus size-4" />
                </span>
              </AccordionTrigger>
              <AccordionContent className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed pb-6 pr-6">
                {item.answer}
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}